import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { logisticsApi } from 'api/logistics';
import { ViewEntityHeader } from 'components/entityViewer/ViewEntityHeader';
import { ViewEntityPage } from 'components/entityViewer/ViewEntityPage';
import { CrossDockPurchaseOrderAdditionalFeesCard } from 'components/logistics/components/crossDockPurchaseOrders/create/CrossDockPurchaseOrderAdditionalFeesCard';
import { CrossDockPurchaseOrderCreatePrimaryCard } from 'components/logistics/components/crossDockPurchaseOrders/create/CrossDockPurchaseOrderCreatePrimaryCard';
import { CrossDockPurchaseOrderEntityHeaderContent } from 'components/logistics/components/crossDockPurchaseOrders/entityView/CrossDockPurchaseOrderEntityHeaderContent';
import { CrossDockPurchaseOrderPrimaryInfoCard } from 'components/logistics/components/crossDockPurchaseOrders/entityView/CrossDockPurchaseOrderPrimaryInfoCard';
import { useCrossDockPurchaseOrderAdditionalFeeTypes } from 'components/logistics/components/crossDockPurchaseOrders/useCrossDockPurchaseOrderAdditionalFeeTypes';
import {
    crossDockPurchaseOrderQueryKeys,
    useCrossDockPurchaseOrderQuery,
} from 'components/logistics/components/crossDockPurchaseOrders/useCrossDockPurchaseOrdersSearch';
import { getAdditionalFeesFooterBreakdown } from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderAdditionalFees';
import {
    getAdditionalFeeChargeTypeLabel,
    getRequiredAdditionalFeeChargeType,
    getRequiredAdditionalFeeLabel,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderAdditionalFeeTypeUtils';
import { validateCrossDockPurchaseOrderUpdate } from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderCreateValidation';
import {
    buildDraftFromCrossDockPurchaseOrder,
    canEditCrossDockPurchaseOrder,
    CROSS_DOCK_PURCHASE_ORDERS_LIST_PATH,
    parseCrossDockPurchaseOrderId,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderUtils';
import {
    useCrossDockOptions,
    useFacilityOptions,
} from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { formatCurrency } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { UnderConstruction } from 'shared';
import { useIsFeatureOn } from 'shared/hooks/featureFlags/useIsFeatureOn';

const CROSS_DOCK_FEES_FEATURE_FLAG = 'CROSS_DOCK_FEES';

function DetailErrorState({ message, onBack, onRetry }) {
    return (
        <div className="flex min-h-[400px] items-center justify-center rounded-md border border-border bg-card px-4 py-12 text-center">
            <div className="flex max-w-md flex-col items-center gap-4">
                <AlertCircle className="h-8 w-8 text-destructive" />
                <div className="flex flex-col items-center gap-2">
                    <span className="text-sm font-medium text-foreground">
                        Unable to load cross dock purchase order
                    </span>
                    <span className="text-sm text-muted-foreground">{message}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                    {onRetry ? (
                        <Button variant="outline" onClick={onRetry}>
                            Retry
                        </Button>
                    ) : null}
                    <Button variant="outline" onClick={onBack}>
                        Back to list
                    </Button>
                </div>
            </div>
        </div>
    );
}

function AdditionalFeesReadOnlyCard({ additionalFees, additionalFeeTypes = [] }) {
    const fees = additionalFees ?? [];
    const requiredChargeType = getRequiredAdditionalFeeChargeType(additionalFeeTypes);
    const requiredFeeLabel = getRequiredAdditionalFeeLabel(additionalFeeTypes);
    const { grandTotal, optionalFees, serviceFeeAmount, subTotal } = getAdditionalFeesFooterBreakdown(fees, {
        requiredChargeType,
    });

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Additional Fees</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
                {fees.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No additional fees recorded.</p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Charge Type</TableHead>
                                <TableHead className="text-right">Quantity</TableHead>
                                <TableHead className="text-right">Unit Rate</TableHead>
                                <TableHead className="text-right">Total</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {optionalFees.map((fee) => (
                                <TableRow key={fee.id ?? fee.chargeType}>
                                    <TableCell>
                                        {getAdditionalFeeChargeTypeLabel(additionalFeeTypes, fee.chargeType)}
                                    </TableCell>
                                    <TableCell className="text-right">{fee.quantity}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(fee.unitRate)}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(fee.totalRate)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={3} className="font-medium">
                                    Sub-total
                                </TableCell>
                                <TableCell className="text-right font-medium">{formatCurrency(subTotal)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={3} className="font-medium">
                                    {requiredFeeLabel}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                    {formatCurrency(serviceFeeAmount)}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={3} className="font-semibold">
                                    Total
                                </TableCell>
                                <TableCell className="text-right font-semibold">{formatCurrency(grandTotal)}</TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}

export function ViewCrossDockPurchaseOrderEntity() {
    const { id } = useParams();
    const history = useHistory();
    const queryClient = useQueryClient();
    const { data: crossDockPurchaseOrdersEnabled, isLoading: isFeatureLoading } =
        useIsFeatureOn(CROSS_DOCK_FEES_FEATURE_FLAG);

    const parsedId = parseCrossDockPurchaseOrderId(id);

    const detailQuery = useCrossDockPurchaseOrderQuery({
        id,
        enabled: Boolean(crossDockPurchaseOrdersEnabled) && parsedId != null,
    });

    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [removedFeeChargeTypes, setRemovedFeeChargeTypes] = useState(() => new Set());
    const isSavingRef = useRef(false);

    const order = detailQuery.data;

    const { data: additionalFeeTypes = [], isLoading: isAdditionalFeeTypesLoading } =
        useCrossDockPurchaseOrderAdditionalFeeTypes({
            enabled: Boolean(crossDockPurchaseOrdersEnabled),
        });

    const { data: facilityOptions = [] } = useFacilityOptions({
        enabled: Boolean(crossDockPurchaseOrdersEnabled),
    });
    const { data: crossDockOptions = [] } = useCrossDockOptions({
        enabled: Boolean(crossDockPurchaseOrdersEnabled),
    });

    const facilityNameById = useMemo(
        () => new Map(facilityOptions.map((option) => [Number(option.value), option.label])),
        [facilityOptions],
    );
    const crossDockNameById = useMemo(
        () => new Map(crossDockOptions.map((option) => [Number(option.value), option.label])),
        [crossDockOptions],
    );

    const facilityName = order ? (facilityNameById.get(Number(order.facilityId)) ?? order.facilityId) : undefined;
    const crossDockName = order ? (crossDockNameById.get(Number(order.crossDockId)) ?? order.crossDockId) : undefined;
    const isDetailLoading = detailQuery.isLoading || (detailQuery.isFetching && !order);

    const canEdit = canEditCrossDockPurchaseOrder(order);
    const editDisabledReason = canEdit ? '' : 'This cross dock purchase order can no longer be edited';

    const validation = useMemo(
        () =>
            draft
                ? validateCrossDockPurchaseOrderUpdate(draft, { additionalFeeTypes, removedFeeChargeTypes })
                : { isValid: false },
        [additionalFeeTypes, draft, removedFeeChargeTypes],
    );

    const handleEdit = useCallback(() => {
        if (!order || !canEdit) {
            return;
        }
        setDraft(buildDraftFromCrossDockPurchaseOrder(order));
        setRemovedFeeChargeTypes(new Set());
        setHasAttemptedSave(false);
        setIsEditing(true);
    }, [canEdit, order]);

    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
        setDraft(null);
        setRemovedFeeChargeTypes(new Set());
        setHasAttemptedSave(false);
    }, []);

    const handleSave = useCallback(async () => {
        if (isSavingRef.current || !parsedId || !draft) {
            return;
        }
        isSavingRef.current = true;
        setHasAttemptedSave(true);

        if (!validation.isValid || !validation.submitBody) {
            isSavingRef.current = false;
            return;
        }

        setIsSaving(true);
        try {
            const response = await logisticsApi.updateCrossDockPurchaseOrder(parsedId, validation.submitBody);
            if (!response?.status) {
                toast.error(response?.msg ?? 'Error updating cross dock purchase order');
                return;
            }

            await queryClient.invalidateQueries({ queryKey: crossDockPurchaseOrderQueryKeys.all });
            toast.success('Cross dock purchase order updated');
            setIsEditing(false);
            setDraft(null);
            setRemovedFeeChargeTypes(new Set());
            setHasAttemptedSave(false);
        } catch (error) {
            toast.error(error?.message ?? 'Error updating cross dock purchase order');
        } finally {
            isSavingRef.current = false;
            setIsSaving(false);
        }
    }, [draft, parsedId, queryClient, validation]);

    if (isFeatureLoading) {
        return null;
    }

    if (!crossDockPurchaseOrdersEnabled) {
        return <UnderConstruction title="Cross Dock Purchase Orders" />;
    }

    const goToList = () => history.push(CROSS_DOCK_PURCHASE_ORDERS_LIST_PATH);

    if (parsedId == null) {
        return <DetailErrorState message="Invalid cross dock purchase order id" onBack={goToList} />;
    }

    if (isDetailLoading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center rounded-md border border-border bg-card px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Loading cross dock purchase order…</span>
                </div>
            </div>
        );
    }

    if (detailQuery.isError) {
        return (
            <DetailErrorState
                message={detailQuery.error?.message || 'Failed to load cross dock purchase order'}
                onBack={goToList}
                onRetry={() => detailQuery.refetch()}
            />
        );
    }

    const header = (
        <ViewEntityHeader
            backHref={CROSS_DOCK_PURCHASE_ORDERS_LIST_PATH}
            backLabel="Back to Cross Dock Purchase Orders"
            entityIdentificationComponent={
                <CrossDockPurchaseOrderEntityHeaderContent
                    order={order}
                    orderId={order?.id ?? id}
                    isEditing={isEditing}
                    isSaving={isSaving}
                    isSaveDisabled={isAdditionalFeeTypesLoading}
                    onCancel={handleCancelEdit}
                    onEdit={canEdit ? handleEdit : undefined}
                    onSave={handleSave}
                    hasValidationErrors={hasAttemptedSave && !validation.isValid}
                    editDisabledReason={editDisabledReason}
                />
            }
        />
    );

    const primaryInformationCard =
        isEditing && draft ? (
            <CrossDockPurchaseOrderCreatePrimaryCard
                draft={draft}
                onDraftChange={setDraft}
                showValidation={hasAttemptedSave}
                primaryErrors={validation.primaryErrors}
                primaryFieldErrors={validation.primaryFieldErrors}
                additionalFeeTypes={additionalFeeTypes}
                feeFieldErrors={validation.feeFieldErrors}
            />
        ) : (
            <CrossDockPurchaseOrderPrimaryInfoCard
                order={order}
                facilityName={facilityName}
                crossDockName={crossDockName}
                additionalFeeTypes={additionalFeeTypes}
            />
        );

    const additionalInformationCards = [
        isEditing && draft ? (
            <CrossDockPurchaseOrderAdditionalFeesCard
                key="additional-fees"
                draft={draft}
                onDraftChange={setDraft}
                additionalFeeTypes={additionalFeeTypes}
                isLoadingFeeTypes={isAdditionalFeeTypesLoading}
                showValidation={hasAttemptedSave}
                feeErrors={validation.feeErrors}
                feeFieldErrors={validation.feeFieldErrors}
                softRemovePersistedFees
                removedFeeChargeTypes={removedFeeChargeTypes}
                onRemovedFeeChargeTypesChange={setRemovedFeeChargeTypes}
            />
        ) : (
            <AdditionalFeesReadOnlyCard
                key="additional-fees"
                additionalFees={order?.additionalFees}
                additionalFeeTypes={additionalFeeTypes}
            />
        ),
    ];

    return (
        <div className="px-4 py-4">
            <ViewEntityPage
                header={header}
                primaryInformationCard={primaryInformationCard}
                additionalInformationCards={additionalInformationCards}
            />
        </div>
    );
}
